// stripWorkspacePrefix comes from @symbo-ls/workspace-shared, stubbed by
// frank's EXTERNAL_PACKAGES at serialization time — reach it via el.call
// instead of a raw import (see TicketDetailModal.js for the full rationale).
import { canonicalTaskPath } from '../functions/ticketUtils.js'

// ── THE filter content — defined ONCE, mounted in TWO shells ───────────────
// Desktop: the fixed floating rail (TicketsSidebar.Middle, below).
// Phone (@mobileL): the shared off-canvas MobileRailSheet — /tickets/board and
// /tickets/epics both mount `MobileRailSheet › Card › Content ›
// TicketsFilterGroups`, so the sheet renders the SAME element tree the rail
// does rather than a parallel copy. Adding or reordering a group here lands on
// both surfaces in one edit; there is no second definition to keep in sync.
export const TicketsFilterGroups = {
  flow: 'y',
  // Flex child in both hosts — a long picker label (a person's full name, an
  // epic title) must ellipsize, never widen the rail into horizontal overflow.
  minWidth: '0',

  // ── FILTER TYPE — issue-type filters: Features / Improvements / Bugs
  // (2026-06-14 per Nika "change name of APPLICATION to Filter Type"). ──
  TicketsTypeGroup: {},

  // ── ASSIGNEE — pick people to filter the board by assignee (multi). ──
  TicketsAssigneeGroup: {},

  // ── ISSUER — pick people to filter the board by reporter (multi). ──
  TicketsIssuerGroup: {},

  // ── EPIC — filter the board by epic (single-select picker). ──
  TicketsEpicGroup: {},

  // ── DRAFTS — show/hide draft tickets (cards dragged out of columns). ──
  TicketsDraftsGroup: {}
}

// Simplified 2026-07-25 per Nika "remove VIEWS and TICKETS from sidebar …
// keep only basic features that match the sidebar filter": the tickets rail
// is FILTERS ONLY — type, assignee, issuer, drafts. The TICKETS nav card,
// PLANNING group and the shared VIEWS switcher are gone (the switcher hides
// itself once /tickets leaves VIEW_NAV — see CanvasViewNav isViewNavRoute).
export const TicketsSidebar = {
  extends: 'AppSidebar',
  tag: 'nav',
  'aria-label': (el) => el.call('polyglot', 'tickets.sidebar.navLabel'),

  // On the page-sidebar rule (shared functions/pageSidebar.js, Nika
  // 2026-10-29): every tasks page's rail takes the side the navbar does not
  // take and carries the one hide button; all tasks pages share one choice.
  // The pages that float this rail (board.js, epics.js) mirror their own
  // `left` anchor.
  pageSidebar: true,

  // No PeopleSection on tickets
  PeopleSection: null,

  // Top section (WorkspaceSwitcher pinned below the AppHeader) removed —
  // the org switcher already lives in the global AppHeader, so showing
  // a second copy at the top of the tickets sidebar was a redundant
  // control. Set to `null` (not deleted) to override the AppSidebar
  // base which still declares a Top slot.
  Top: null,

  Middle: {
    overflow: 'auto',
    flex: '1',
    minWidth: '0',

    // LIGHT-MODE SURFACE SWAP (2026-08-23 per Nika "in light mode backgrounds
    // are inverted") — mirror of the change in TicketsKanban.js: in light this
    // card wears the surface the kanban columns used to have (gray16.95+6)
    // and the columns wear `overlayStrong`. Since the sidebar card pass (Nika
    // 2026-10-30 "A: navbar glass") that fill comes from the column's sidebar
    // card itself — the brand `navbar` @light fill is the same gray16.95+6 —
    // so this card keeps no copy of it (components/lightSurfaceSwap.test.js).

    // Filter groups — bare key onto the single source above. The phone sheet
    // mounts this exact component, so the two never drift.
    TicketsFilterGroups: {}
  }
}

// ── Sidebar item — extends SidebarRow with route-based active state ──

// Active when this item's view is the one showing in the PageSlot. Sidebar
// items open the selected /tickets/* route in the shell PageSlot by setting
// `root._canvasSlot` (the same slot /studio + /infra use — unified 2026-07-10
// per Nika "use PageSlot for all opening across all pages", replacing the old
// in-canvas TicketsViewHost). The kanban Board is the "no view" base, so the
// Board row is active whenever nothing is selected.
const _itemActive = (el, s) => {
  // Everything normalizes to the canonical /tasks form so items match
  // whichever alias (/tasks/* or legacy /tickets/*) the slot/URL carries.
  const view = canonicalTaskPath((s.root?._canvasSlot || '').split('?')[0])
  const href = canonicalTaskPath(el.href || '')
  if (href === '/tasks/board') return !view
  if (view) return view === href
  return (
    canonicalTaskPath(
      el.call('stripWorkspacePrefix', s.root?.currentPath || s.root?.route || '/')
    ) === href
  )
}

export const TicketsSidebarItem = {
  extends: 'SidebarRow',
  // Board is ALWAYS listed. Clicking it clears `_canvasSlot` to return to the
  // kanban base view (see onClick) — a meaningful action regardless of how the
  // Tickets section was reached. The previous hide (Board hidden whenever
  // root.selectedPage matched /tickets) wrongly removed the row when the user
  // navigated in via the apps dropdown/icon — that path sets selectedPage to a
  // /tickets route too, so the Board nav row vanished from the side nav.
  // (ticket 6a331f77 — "Board is missing from the side nav".)
  // Expose the active view to assistive tech alongside the visual wash.
  attr: { 'aria-current': (el, s) => (_itemActive(el, s) ? 'page' : undefined) },
  padding: 'Z A',
  round: 'radiusPill',
  fontSize: 'A',
  // Active state matches the FILTER TYPE rows: frosted white-wash pill + title
  // text. Driven by the .isActive CONDITIONAL CLASS — NOT reactive background/
  // color/fontWeight factories, which emit INLINE node styles that clobber the
  // :hover wash (an inline style beats any :hover class), leaving the row with no
  // visible hover feedback. The class-based active state lets :hover render.
  // (2026-06-15 per Nika "make all active state colors on sidebar just like
  // FILTER TYPE".)
  isActive: (el, s) => _itemActive(el, s),
  color: 'caption',
  fontWeight: '500',
  '.isActive': {
    background: 'selected',
    color: 'title',
    fontWeight: '700'
  },
  ':hover': {
    background: 'hover',
    color: 'title'
  },

  Icon: {
    // Fixed square slot, centered glyph — glyph aspect ratios differ per
    // icon, so without the reserved slot every row's label started at a
    // slightly different x (2026-08-06 per Nika "fix filter and + icon
    // alignment in buttons on /tasks").
    boxSize: 'A',
    minWidth: 'A',
    flexShrink: '0',
    align: 'center center',
    color: (el, s) => (_itemActive(el.parent, s) ? 'title' : 'caption')
  },

  // Open the selected /tickets/* route in the shell PageSlot (unified 2026-07-10
  // per Nika "use PageSlot for all opening across all pages"). Setting
  // `root._canvasSlot` mounts that page in the PageSlot's tickets variant —
  // beside the floating rail (the rail stays the switcher). The kanban Board is
  // the base view, so the Board row clears the slot (back to the kanban).
  onClick: (e, el, s) => {
    e.preventDefault()
    const root = s.root
    if (!root) return
    if (canonicalTaskPath(el.href || '') === '/tasks/board') {
      root.update({ _canvasSlot: null })
      return
    }
    root.update({ _canvasSlot: el.href })
  },

  // Local override of the shared SidebarRow's Label. That component lives in
  // `packages/workspace` and renders `el.parent.label` verbatim, so a row
  // carrying a bundle KEY would ship the raw dotted key. Resolving here keeps
  // the change inside this package (CLAUDE.md: prefer a local override), and
  // polyglot returns a non-key verbatim so a server-supplied label still works.
  Label: { text: (el) => el.call('polyglot', el.parent?.label || '') },

  // No row "···" menu — the pin/customize actions belonged to the removed
  // TICKETS nav list (rail simplified to filters-only, 2026-07-25 per Nika).
  MoreBtn: null
}

// ── Sidebar group shell ──

export const TicketsSidebarGroup = {
  flow: 'y',
  gap: 'Z',
  marginBottom: 'A',

  Header: {
    flow: 'x',
    align: 'center left',
    padding: 'V 0',

    CaptionTitle: {
      flex: '1',
      fontSize: 'Y2',
      Text: {
        // The group's `label` is a bundle KEY (this component is extended with
        // static labels only); polyglot returns a non-key verbatim.
        text: (el) => el.call('polyglot', el.parent?.parent?.parent?.label || '')
      }
    }
  }
}

// ── Type filters (per Nika 2026-06-10 "in sidebar add filters to filter by
// type") — a rail filter row keyed on root.filterIssueType, which already
// threads through every board fetch (_buildMongoFilter → f.type).
export const TicketsTypeBtn = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  padding: 'Z A',
  round: 'radiusPill',
  minHeight: 'controlHCompact',
  cursor: 'pointer',
  fontSize: 'A',
  textDecoration: 'none',
  fontWeight: (el, s) =>
    (s.root?.filterIssueType || 'all') === (el.value || 'all') ? '700' : '500',
  // Active state mirrors the /files FilesSidebar rows (2026-06-14 per Nika
  // "active state matches FilesSidebar"): a frosted white-wash pill + title
  // text when active; caption text + color-only hover otherwise.
  background: (el, s) =>
    (s.root?.filterIssueType || 'all') === (el.value || 'all') ? 'selected' : 'transparent',
  color: (el, s) =>
    (s.root?.filterIssueType || 'all') === (el.value || 'all') ? 'title' : 'caption',
  borderWidth: '0',
  ':hover': {
    background: 'hover',
    color: 'title'
  },
  // Every filter row of the rail eases its hover fill on the same motion
  // token (brand motion budget: hovers on defaultBezier).
  transition: 'background Z defaultBezier',
  // State-only — the board page's subscribed watcher owns the refetch
  // (_loadBoard, sequence-guarded). Calling reloadTickets here as well
  // created dueling async writers whose stale/empty wave could land last
  // and blank the filtered board.
  // Toggle: clicking the active type again clears the filter (no "All" row).
  // Press feedback completes the row's interactive states (hover exists
  // above): a slightly deeper wash while the pointer is down.
  ':active': {
    background: 'selected'
  },
  onClick: (e, el, s) => {
    const cur = s.root?.filterIssueType || null
    if (el.value === 'all') {
      s.root.update({ filterIssueType: null })
    } else {
      s.root.update({ filterIssueType: cur === el.value ? null : el.value })
    }
  },
  Icon: {
    // A fixed, centered icon slot, so the labels of every row start on one
    // line whatever the glyph's own width.
    icon: (el) => el.parent?.icon || 'state',
    boxSize: 'A',
    minWidth: 'A',
    flexShrink: '0',
    align: 'center center'
  },
  Label: {
    tag: 'span',
    text: (el) => el.call('polyglot', el.parent?.label || ''),
    flex: '1'
  },
  Count: {
    tag: 'span',
    text: (el, s) => {
      const counts = s.root?.totalByType || {}
      const val = el.parent?.value || 'all'
      return counts[val] !== undefined ? String(counts[val]) : ''
    },
    opacity: '0.6',
    fontSize: 'Z2'
  }
}

export const TicketsTypeGroup = {
  extends: 'TicketsSidebarGroup',
  label: 'tickets.sidebar.group.filterType',
  List: {
    flow: 'y',
    gap: 'V',
    childExtends: 'TicketsTypeBtn',
    children: [
      { value: 'all', label: 'tickets.sidebar.type.all', icon: 'grid' },
      { value: 'bug', label: 'tickets.sidebar.type.bugs', icon: 'bug' },
      { value: 'task', label: 'tickets.sidebar.type.tasks', icon: 'check' }
    ]
  },
  DividerTop: {
    height: '1px',
    background: 'line',
    margin: 'V 0',
    width: '100%'
  },
  FeaturesBtn: {
    extends: 'TicketsTypeBtn',
    value: 'user_story',
    label: 'tickets.sidebar.type.featuresAndStories',
    icon: 'magicstar',
    padding: 'A',
    margin: 'V 0 0 0'
  },
  DividerBottom: {
    height: '1px',
    background: 'line',
    margin: 'V 0',
    width: '100%'
  }
}

// ── ASSIGNEE / ISSUER filters — MULTI-SELECT person pickers (2026-07-25 per
// Nika "allow multiple selects in assignee and assigned filters"). The row
// shows the picked people ("Anyone" / a name / "Name +N"); clicking opens the
// house ContextDropdown whose rows are built REACTIVELY via the registered
// itemsFn (ticketsAssigneePickItems / ticketsIssuerPickItems) so the check
// marks track the live filter arrays while the menu stays open. Picking
// routes through `applyTicketsFilterPick` which TOGGLES the email in the
// array. The board's subscribed watcher refetches — STATE-ONLY here (a manual
// reloadTickets would race the watcher — same hazard noted on TicketsTypeBtn).

// Explicitly picked emails (array). Category values ('all'/'team'/'agents'/
// 'me', set by other assignee controls) read as "Anyone" here.
const _pickedEmails = (root, field) => {
  const v = root?.[field]
  if (Array.isArray(v)) return v
  if (!v || ['all', 'team', 'agents', 'me'].includes(v)) return []
  return [v]
}

const _pickedPerson = (root, email) =>
  (root?.people || []).find(
    (p) => (p.email || '').toLowerCase() === String(email).toLowerCase()
  ) || { email }

// INTERACT-1: the Assignee/Issuer/Epic filter Pickers open the shared
// ContextDropdown (root.contextDropdown), which ALREADY implements full
// keyboard nav — Down/Up/Home/End cycling (Menu.onKeydown → menuLevelNav)
// and Escape-close (same onKeydown) — see packages/shared/components/
// ContextDropdown.js. DOM-verified this menu's keyboard model works
// correctly everywhere else it's used (chat/home/files context menus).
// The bug is narrower than "no keyboard nav": nothing ever moves DOM focus
// INTO the menu when a Picker opens it, so every arrow-key/Escape press
// keeps firing on the still-focused Picker `<button>` instead of bubbling
// through Menu's onKeydown at all — the menu's own handlers never see the
// keystroke. Fixed at the opener (here), not in the shared component: reuse
// the exact bounded-poll "focus the first row once it exists in the DOM"
// technique ContextDropdownItem.scope.openSubmenu already uses for its own
// submenu flyout (same shape of problem — the row doesn't exist until the
// state commit lands and the previously display:none panel paints).
// `[data-key="ContextDropdown"]` is the same selector packages/canvas/
// functions/canvasContextMenu.js already relies on to find this ONE
// globally-mounted instance (it isn't a DOM descendant of the Picker button
// that opens it, so there's no local `el.node` to query from here).
//
// Real DOM focus movement — NOT aria-activedescendant — deliberately
// matches the pattern every other ContextDropdown consumer in this app
// already uses (menuLevelNav moves real focus onto each `[role="menuitem"]`
// row as you arrow through). Layering a parallel aria-activedescendant
// scheme on top would contradict that established, working contract rather
// than reuse it, and is redundant once real focus is already moving through
// the rows — assistive tech gets a real focus event per row either way.
const _focusContextMenuFirstItem = () => {
  if (typeof document === 'undefined') return
  let tries = 0
  const tryFocus = () => {
    const first = document.querySelector('[data-key="ContextDropdown"] [role="menuitem"]')
    if (first && first.focus) first.focus()
    else if (++tries < 10) window.setTimeout(tryFocus, 30)
  }
  window.setTimeout(tryFocus, 0)
}

const _personPicker = ({ field: pickerField, itemsFn: pickerItemsFn, glyph }) => ({
  // Factory closure captures — `pickerField` and `pickerItemsFn` are read
  // inside reactive props (background/color/Label.text) and the onClick
  // handler below. Frank strips JS closures at serialization time;
  // `scope: {}` travels the captured values with the element so the bare
  // identifier references stay resolvable at runtime (FA205 —
  // factory-closure).
  //
  // Named `pickerField`/`pickerItemsFn`, NOT the shorter `field`/`itemsFn` —
  // frank's scope-rewriter matches bare-identifier reads against the scope
  // KEY NAME across the whole emitted bundle, not just this component's own
  // subtree. `field` collides with the many unrelated local PARAMETERS
  // named `field` elsewhere in this package (patchTicketField,
  // _showField, _personPickItems, …) and the rewrite corrupted one of
  // those signatures (`function(root, field)` → `function(root, this.field)`)
  // the first time this was tried. Picking collision-proof names sidesteps
  // the bug outright (see tickets/smbls.md for the framework-level report).
  scope: { pickerField, pickerItemsFn },
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  width: '100%',
  padding: 'Z A',
  round: 'radiusPill',
  minHeight: 'controlHCompact',
  cursor: 'pointer',
  borderWidth: '0',
  fontSize: 'A',
  textAlign: 'left',
  // FilesSidebar active wash when at least one person is picked.
  background: (el, s) => (_pickedEmails(s.root, pickerField).length ? 'selected' : 'transparent'),
  color: (el, s) => (_pickedEmails(s.root, pickerField).length ? 'title' : 'caption'),
  ':hover': {
    background: 'hover',
    color: 'title'
  },
  // Same hover motion as every filter row (see TicketsTypeBtn).
  transition: 'background Z defaultBezier',

  // First picked person's avatar (when anyone is selected) …
  //
  // The `hide:`/`hide:` pair below is deliberate and stays. An earlier note
  // here justified it by claiming `if:` is inert in this v3.14 build; that
  // claim is stale — smbls create.js evaluates `if:` before `cacheNode`,
  // returns without a node when it is false, and registers a reactive
  // `registerIfEffect` so the element can appear later. It is NOT changed
  // here only because this pair is measured working and a hidden <img>
  // carrying NO src attribute issues no request either.
  //
  // What DID have to change is the `src` fallback. It answered `''` for the
  // nobody-picked case, and an empty src is not "no image": the browser
  // resolves it against the DOCUMENT URL and fires a real, failing request.
  // smbls's attribute effect calls `removeAttribute` when a src factory
  // answers null (create.js), so absence was already handled correctly and
  // the `|| ''` was undoing it. Six catalog specimens reported the resulting
  // image error, all of them this one row (2026-09-20). Pinned by
  // __tests__/pickerAvatarEmptySrc.test.js.
  Avatar: {
    extends: 'Avatar',
    width: 'A',
    height: 'A',
    minWidth: 'A',
    hide: (el, s) => !(_pickedEmails(s.root, pickerField).length > 0),
    src: (el, s) => {
      const emails = _pickedEmails(s.root, pickerField)
      return emails.length ? el.call('avatarUrlFor', emails[0]) : null
    }
  },
  // … or a generic glyph when showing "Anyone".
  Icon: {
    extends: 'Icon',
    // `name:`, not `icon:` — #234 renamed Icon's prop from `icon` to `name`
    // everywhere else in this file (Chevron below, TicketsEpicPicker's own
    // Icon/Chevron); this dynamic (`glyph` variable, not a literal) case was
    // missed by the mechanical sweep. Under the parent IconText.Icon `if:`
    // gate, `icon: X` never creates the child at all — the people/user glyph
    // silently never rendered.
    name: glyph,
    boxSize: 'A',
    minWidth: 'A',
    color: 'caption',
    hide: (el, s) => !!_pickedEmails(s.root, pickerField).length
  },

  Label: {
    tag: 'span',
    flex: '1',
    // Explicit flex-child floor — the picker sits in a G1 rail on desktop and a
    // 86vw sheet on phones; a long full name must ellipsize, never widen either.
    minWidth: '0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'left',
    text: (el, s) => {
      const emails = _pickedEmails(s.root, pickerField)
      if (!emails.length) return el.call('polyglot', 'tickets.filter.anyone')
      const first = _pickedPerson(s.root, emails[0])
      const name = first.name || first.username || first.email
      return emails.length > 1 ? `${name} +${emails.length - 1}` : name
    }
  },
  Chevron: { extends: 'Icon', name: 'arrowAngleDown', boxSize: 'Z1', color: 'caption' },

  onClick: (e, el, s) => {
    e.stopPropagation()
    const root = s.root
    if (!root || !el.node) return
    // Agents list below humans in the picker — the shared roster is empty
    // until /team-and-agents has run, so kick the tickets-side one-shot
    // fill (fire-and-forget; on landing it refreshes the open menu).
    el.call('ensureTicketsAgentsRoster')
    const r = el.node.getBoundingClientRect()
    root.update({
      contextDropdown: {
        open: true,
        x: Math.round(r.left),
        y: Math.round(r.bottom + 6),
        // Explicit `el.scope.pickerItemsFn` — the scope-rewriter's
        // bare-identifier rewrite only fires for the CSS-prop-style reads it
        // statically recognizes (background/color/text); a shorthand object
        // property buried inside an onClick body is outside that pattern and
        // is left as a bare, unresolvable read (FA205). Reading it off
        // `el.scope` directly needs no rewrite at all.
        itemsFn: el.scope.pickerItemsFn,
        actionFn: 'applyTicketsFilterPick'
      }
    })
    // INTERACT-1: move real DOM focus into the menu so Down/Up/Home/End +
    // Escape (already implemented on the shared ContextDropdown's Menu) are
    // actually reachable — see _focusContextMenuFirstItem's header comment.
    _focusContextMenuFirstItem()
  }
})

export const TicketsAssigneePicker = _personPicker({
  field: 'filterAssignee',
  itemsFn: 'ticketsAssigneePickItems',
  glyph: 'people'
})

export const TicketsIssuerPicker = _personPicker({
  field: 'filterIssuer',
  itemsFn: 'ticketsIssuerPickItems',
  glyph: 'user'
})

// ── DRAFTS — an ON/OFF SWITCH for draft tickets (cards dragged out of a
// column get the 'draft' status, hidden by default — 2026-07-25 per Nika
// "make drafts on/off toggle"). The row uses the house SwitchInput (the
// iOS-style switch every admin toggle migrated to); clicking anywhere on
// the row flips it. Works in both freeform and columns modes; persisted
// via setTicketsBoardPref.
export const TicketsDraftsToggle = {
  flow: 'x',
  align: 'center space-between',
  gap: 'Z1',
  padding: 'Z A',
  round: 'radiusPill',
  minHeight: 'controlHCompact',
  cursor: 'pointer',
  fontSize: 'A',
  color: (el, s) => (s.root?._showDrafts ? 'title' : 'caption'),
  ':hover': {
    background: 'hover',
    color: 'title'
  },
  // Same hover motion as every filter row (see TicketsTypeBtn).
  transition: 'background Z defaultBezier',
  onClick: (e, el, s) => {
    el.call('setTicketsBoardPref', { _showDrafts: !s.root?._showDrafts })
  },
  Left: {
    flow: 'x',
    align: 'center left',
    gap: 'Z1',
    minWidth: '0',
    Icon: { name: 'edit', boxSize: 'A', minWidth: 'A' },
    Label: { tag: 'span', minWidth: '0', text: '{{ tickets.sidebar.drafts.label | polyglot }}' }
  },
  Switch: {
    extends: 'SwitchInput',
    // `onCheck` (not isChecked) — v3.14 freezes ordinary function props at
    // create; on*-prefixed props stay callable so the switch's reactive
    // track/knob factories re-dispatch per repaint (SwitchInput contract).
    onCheck: (el, s) => !!s.root?._showDrafts,
    aria: { label: (el) => el.call('polyglot', 'tickets.sidebar.drafts.aria') },
    onClick: (e, el, s) => {
      e.stopPropagation()
      el.call('setTicketsBoardPref', { _showDrafts: !s.root?._showDrafts })
    }
  }
}

export const TicketsDraftsGroup = {
  extends: 'TicketsSidebarGroup',
  label: 'tickets.sidebar.group.drafts',
  List: {
    flow: 'y',
    gap: 'V',
    Toggle: { extends: 'TicketsDraftsToggle' }
  }
}

// ── EPIC — filter the board by epic (2026-07-25 per Nika "keep epics and
// add epics in filter sidebar"). Same picker chrome as Assignee/Issuer;
// rows come from the reactive ticketsEpicPickItems itemsFn (epics list is
// lazy-loaded into root.epicsTree on first open). Single-select — picking
// the active epic again clears.
export const TicketsEpicPicker = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  width: '100%',
  padding: 'Z A',
  round: 'radiusPill',
  minHeight: 'controlHCompact',
  cursor: 'pointer',
  borderWidth: '0',
  fontSize: 'A',
  textAlign: 'left',
  background: (el, s) => (s.root?.filterEpic ? 'selected' : 'transparent'),
  color: (el, s) => (s.root?.filterEpic ? 'title' : 'caption'),
  ':hover': {
    background: 'hover',
    color: 'title'
  },
  // Same hover motion as every filter row (see TicketsTypeBtn).
  transition: 'background Z defaultBezier',
  Icon: { extends: 'Icon', name: 'star', boxSize: 'A', minWidth: 'A', color: 'caption' },
  Label: {
    tag: 'span',
    flex: '1',
    // Same flex-child floor as the person pickers — epic titles are long.
    minWidth: '0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'left',
    text: (el, s) => s.root?.filterEpic || el.call('polyglot', 'tickets.filter.anyEpic')
  },
  Chevron: { extends: 'Icon', name: 'arrowAngleDown', boxSize: 'Z1', color: 'caption' },
  onClick: (e, el, s) => {
    e.stopPropagation()
    const root = s.root
    if (!root || !el.node) return
    const r = el.node.getBoundingClientRect()
    // Lazy-load the epic-type ticket rows once (same consolidated loader the
    // detail modal + Manage Epics modal use — see loadEpicsTree). The picker
    // itself always shows the wider ticketsEpicNames union (epicsTree ∪
    // free-text epicName values already on tickets), so this fetch only
    // needs to run when epicsTree hasn't landed yet.
    if (!root.epicsTree?.length) {
      el.call('loadEpicsTree').then(() => {
        // Refresh the open menu so the fetched rows appear in place.
        const dd = root.contextDropdown
        if (dd) root.update({ contextDropdown: { ...dd } })
      })
    }
    root.update({
      contextDropdown: {
        open: true,
        x: Math.round(r.left),
        y: Math.round(r.bottom + 6),
        itemsFn: 'ticketsEpicPickItems',
        actionFn: 'applyTicketsFilterPick'
      }
    })
    // INTERACT-1: see _focusContextMenuFirstItem's header comment (same fix
    // as TicketsAssigneePicker/TicketsIssuerPicker above).
    _focusContextMenuFirstItem()
  }
}

export const TicketsEpicGroup = {
  extends: 'TicketsSidebarGroup',
  label: 'tickets.sidebar.group.epic',
  List: {
    flow: 'y',
    gap: 'V',
    Picker: { extends: 'TicketsEpicPicker' }
  }
}

export const TicketsAssigneeGroup = {
  extends: 'TicketsSidebarGroup',
  label: 'tickets.sidebar.group.assignee',
  List: {
    flow: 'y',
    gap: 'V',
    Picker: { extends: 'TicketsAssigneePicker' }
  }
}

export const TicketsIssuerGroup = {
  extends: 'TicketsSidebarGroup',
  label: 'tickets.sidebar.group.issuer',
  List: {
    flow: 'y',
    gap: 'V',
    Picker: { extends: 'TicketsIssuerPicker' }
  }
}
